import React, { useEffect, useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { RootState } from '../../store/store';
import { PortalNavList } from '../../design-system/chrome';
import { PortalEnvironment } from '../../design-system/environments';
import { KID_ICON_PATHS } from '../../design-system/kid-nav';
import { homePathForRole } from '../config/routes';
import ContextUnavailable from '../../core/identity-access/components/ContextUnavailable';
import KidExitDialog from '../../core/identity-access/components/KidExitDialog';
import { useSignOut } from '../../core/identity-access/store/signOut';
import { LearnerSpaceProvider, firstNameOf } from '../../verticals/iqmax/learner/LearnerSpace';
import SafetyReportAction from '../../verticals/iqmax/components/SafetyReportAction';
import '../../verticals/iqmax/learner/learner.css';

/**
 * The child space — the redesign's third chrome, now a real one.
 *
 * GAP-S01-01 (CTO, 12 Oct 2026): there are no child accounts. This shell is
 * the parent's session in kid mode, with one learner active. So it is guarded
 * on what the server says — a PARENT whose context is in kid mode — and not on
 * a STUDENT role nobody holds. It has no account menu and no learner switcher
 * (a sibling never appears here, STU-16-FR-06). Leaving the space, or moving to
 * a sibling, is a Parent step-up with the identity provider (DEC-S2-S01-04).
 *
 * It has two ways to stop, and they are different things (DEC-S2-S01-06). "Salir
 * de mi espacio" returns to the Parent and asks for the Parent. "Cerrar sesión"
 * ends the session on this device and only here: this device's tokens revoked,
 * caches cleared, the lock left in place, so the next sign-in here lands back in
 * this space, never in the Parent's, and the Parent's other devices carry on.
 *
 * A refresh keeps the child here: App reads the context back from the server
 * before anything renders, and the shell waits for that answer. If the server
 * cannot answer, it says so rather than reading that as "not in kid mode".
 */
interface Props {
  environment: PortalEnvironment;
}

const KidShell: React.FC<Props> = ({ environment }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const kidMode = useSelector((state: RootState) => state.context.kidMode);
  const restored = useSelector((state: RootState) => state.context.restored);
  const restoreFailed = useSelector((state: RootState) => state.context.restoreFailed);
  const activeContext = useSelector((state: RootState) => state.context.activeContext);
  const [sideOpen, setSideOpen] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);
  const signOut = useSignOut();

  useEffect(() => setSideOpen(false), [location.pathname]);

  if (role === 'PARENT' && restoreFailed) return <ContextUnavailable />;
  if (role === 'PARENT' && !restored) return null;
  if (role !== 'PARENT' || !kidMode) {
    // Not in kid mode: the child's space is not a page a parent visits by URL.
    return <Navigate to={homePathForRole(role)} replace />;
  }

  const firstName = firstNameOf(activeContext?.studentName);
  const learnerId = activeContext?.studentId;
  // The class door carries its own report action, about that class; everywhere
  // else in the space the shell offers it (UC-S09). Either draws nothing while
  // the server's `safety_reports` intake is off.
  const onClassDoor = location.pathname.startsWith('/student/join/');

  return (
    <LearnerSpaceProvider value={{ studentId: activeContext?.studentId ?? null, firstName }}>
      <div className={environment.scope}>
        <div className="top">
          <div className="top-in">
            <button className="mn-b" onClick={() => setSideOpen((v) => !v)} aria-label="Menú">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
                width={21}
                height={21}
                dangerouslySetInnerHTML={{ __html: KID_ICON_PATHS.menu }}
              />
            </button>
            <Link to={environment.home} className="logo">
              i<span>Q</span>Maxx
            </Link>
          </div>
        </div>

        <div className="shell">
          <nav className={`side${sideOpen ? ' open' : ''}`}>
            <div className="side-h">
              <div className="side-av" />
              <div>
                <b>{firstName || 'Hola'}</b>
                <span>Mi espacio</span>
              </div>
            </div>

            <PortalNavList environment={environment} onNavigate={() => setSideOpen(false)} />

            <div className="side-sp" />
            <button className="side-out" onClick={() => setExitOpen(true)}>
              Salir de mi espacio
            </button>
            <button className="side-out" onClick={() => signOut()}>
              Cerrar sesión
            </button>
          </nav>

          <div className="wrap">
            {activeContext ? (
              <>
                <Outlet />
                {learnerId && !onClassDoor && (
                  <div className="lrn-report">
                    <SafetyReportAction studentId={learnerId} />
                  </div>
                )}
              </>
            ) : (
              <div className="empty" role="status">
                <div className="empty-e" aria-hidden="true">
                  🙋
                </div>
                <h2 className="h2">Llama a un adulto</h2>
                <p>Este espacio necesita que un adulto lo vuelva a preparar.</p>
              </div>
            )}
          </div>
        </div>

        {exitOpen && (
          <KidExitDialog
            onClose={() => setExitOpen(false)}
            onExited={() => {
              setExitOpen(false);
              navigate('/parent/dashboard', { replace: true });
            }}
          />
        )}
      </div>
    </LearnerSpaceProvider>
  );
};

export default KidShell;
